import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAnalysisJobs, useDeleteAnalysisJob } from '@/hooks/useScriptAnalysis';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/common/QueryState';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { FileUp, Clapperboard, Trash2, Clock, CheckCircle, AlertTriangle, Loader2, Archive, ArchiveRestore, ChevronDown, ChevronRight, GitBranch, ArrowLeft } from 'lucide-react';

/** V3 · texto del resumen del diff de una versión incremental (o null si no aplica). */
function diffChipText(job: any): string | null {
  const d = job?.diff_summary;
  if (!d || job?.reanalysis_mode !== 'incremental') return null;
  return `${d.igual} iguales · ${d.editada} editadas · ${d.nueva} nuevas${d.reused_pct != null ? ` · ${d.reused_pct}% reutilizado` : ''}`;
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  completed: { label: 'Completado', color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30', icon: <CheckCircle size={14} /> },
  processing: { label: 'Procesando', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30', icon: <Loader2 size={14} className="animate-spin" /> },
  needs_continue: { label: 'Continuando', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30', icon: <Loader2 size={14} className="animate-spin" /> },
  failed: { label: 'Error', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30', icon: <AlertTriangle size={14} /> },
  pending: { label: 'Pendiente', color: 'bg-pelip-bg-elev-2 text-pelip-text-muted border-pelip-border-strong', icon: <Clock size={14} /> },
  cancelled: { label: 'Cancelado', color: 'bg-pelip-bg-elev-2 text-pelip-text-faint border-pelip-border-strong', icon: <Clock size={14} /> },
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getProjectName(fileName: string): string {
  return fileName.replace(/\.(pdf|fdx|fountain|txt)$/i, '').replace(/\s+v\d+$/i, '');
}

export default function ProjectsPage() {
  const { data: jobs, isLoading, isError, refetch } = useAnalysisJobs();
  const deleteJob = useDeleteAnalysisJob();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showArchived, setShowArchived] = useState(false);
  // V3 · clave del proyecto cuyo historial de versiones está desplegado (null = ninguno).
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // Multi-seat: nombres de los dueños AJENOS (proyectos compartidos por la org) para
  // mostrar "de {dueño}". Solo se consultan los user_id que no son el mío.
  const ownerIds = useMemo(
    () => [...new Set((jobs || []).map((j: any) => j.user_id).filter((id: string) => id && id !== user?.id))],
    [jobs, user?.id],
  );
  const { data: ownerNames } = useQuery({
    queryKey: ['project-owner-names', ownerIds],
    enabled: ownerIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from('profiles').select('user_id, name').in('user_id', ownerIds);
      return Object.fromEntries((data || []).map((p: any) => [p.user_id, p.name]));
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4 py-6">
        <Skeleton className="h-10 w-64" />
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
    );
  }

  if (isError) return <QueryError title="No se pudieron cargar los proyectos" onRetry={refetch} />;

  // Group jobs by PROJECT (V3: project_id agrupa versiones; fallback a nombre de
  // archivo para jobs muy antiguos sin project_id — la migración lo backfilleó, así
  // que en la práctica siempre hay project_id).
  const projectMap = new Map<string, NonNullable<typeof jobs>>();
  for (const job of jobs || []) {
    const key = (job as any).project_id || getProjectName(job.file_name);
    if (!projectMap.has(key)) projectMap.set(key, []);
    projectMap.get(key)!.push(job);
  }

  // Versión más alta primero (empate → más reciente).
  const byVersionDesc = (a: any, b: any) =>
    ((b.version ?? 1) - (a.version ?? 1)) || (new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const allProjects = [...projectMap.entries()].map(([key, projectJobs]) => {
    // Best job = versión completada más alta, o el más reciente si ninguna completó.
    const completed = projectJobs.filter((j: any) => j.status === 'completed');
    const best = completed.length > 0
      ? completed.slice().sort(byVersionDesc)[0]
      : projectJobs.slice().sort(byVersionDesc)[0];
    // Proyecto archivado = todos sus análisis completados están archivados (libera plaza).
    const isArchived = completed.length > 0 && completed.every((j: any) => j.archived_at);
    const name = getProjectName(best.file_name);
    // Historial: todas las versiones ordenadas (más nueva primero).
    const versions = projectJobs.slice().sort(byVersionDesc);
    return { key, name, jobs: projectJobs, best, isArchived, versions };
  });
  const activeProjects = allProjects.filter((p) => !p.isArchived);
  const archivedProjects = allProjects.filter((p) => p.isArchived);
  const projects = showArchived ? archivedProjects : activeProjects;

  async function setArchived(jobIds: string[], archived: boolean) {
    const { error } = await supabase.from('analysis_jobs')
      .update({ archived_at: archived ? new Date().toISOString() : null })
      .in('id', jobIds);
    queryClient.invalidateQueries({ queryKey: ['analysis-jobs'] });
    if (error) toast.error('No se pudo archivar', { description: error.message });
    else toast.success(archived ? 'Proyecto archivado — plaza liberada' : 'Proyecto reactivado');
  }

  // Borra el proyecto COMPLETO, o sea TODAS sus versiones, igual que hace el botón de
  // archivar de al lado. Antes borraba solo la versión actual (`best.id`), así que en un
  // proyecto con varias versiones el usuario confirmaba "se eliminarán todos los datos",
  // se borraba una sola y el proyecto seguía en la lista con las demás: se leía como
  // "no se puede borrar" (caso real de Edwin, 3 versiones). Las versiones hijas van
  // primero para no dejar huérfanos a medias si algo falla por el camino.
  async function handleDelete(jobIds: string[]) {
    const orden = [...jobIds].reverse(); // las últimas versiones primero, el original al final
    const fallos: string[] = [];
    for (const id of orden) {
      try {
        await deleteJob.mutateAsync(id);
      } catch (e) {
        fallos.push(e instanceof Error ? e.message : String(e));
        console.error('[ProjectsPage] delete failed', id, e);
      }
    }
    queryClient.invalidateQueries({ queryKey: ['analysis-jobs'] });
    if (fallos.length === 0) {
      toast.success(jobIds.length > 1 ? `Proyecto eliminado (${jobIds.length} versiones)` : 'Proyecto eliminado');
    } else {
      toast.error('No se pudo borrar el proyecto entero', {
        description: fallos[0],
        duration: 10000,
      });
    }
  }

  return (
    <div className="max-w-[900px] mx-auto py-8 px-4">
      <Link to="/" className="inline-flex items-center gap-1.5 mb-4 text-pelip-text-faint hover:text-pelip-text transition-colors">
        <ArrowLeft size={14} />
        <span className="text-xs font-mono uppercase tracking-wider">Volver</span>
      </Link>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-light text-pelip-text tracking-tight">{showArchived ? 'Proyectos archivados' : 'Proyectos'}</h1>
          <p className="text-sm text-pelip-text-faint mt-1">
            {activeProjects.length} activo{activeProjects.length === 1 ? '' : 's'}
            {archivedProjects.length > 0 && (
              <button onClick={() => setShowArchived(!showArchived)} className="ml-2 underline hover:text-pelip-text">
                {showArchived ? '← Ver activos' : `Ver archivados (${archivedProjects.length})`}
              </button>
            )}
          </p>
        </div>
        <Link to="/subir">
          <Button className="gap-2">
            <FileUp size={16} />
            Nuevo Análisis
          </Button>
        </Link>
      </div>

      {projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24">
          <Clapperboard size={48} className="text-pelip-text-faint mb-4" />
          <h2 className="text-xl font-bold text-pelip-text mb-2">Sin proyectos</h2>
          <p className="text-pelip-text-muted mb-6">Sube un guión para comenzar</p>
          <Link to="/subir">
            <Button className="gap-2">
              <FileUp size={16} />
              Subir Guión
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map(({ key: projKey, name, jobs: projectJobs, best, isArchived, versions }) => {
            const status = statusConfig[best.status] || statusConfig.pending;
            const isClickable = best.status === 'completed';
            const scenesText = best.total_scenes ? `${best.total_scenes} escenas` : '';
            const isExpanded = expandedKey === projKey;
            const bestChip = diffChipText(best);

            return (
              <Card
                key={best.id}
                className={`border-pelip-border bg-pelip-bg-elev/50 transition-colors ${isClickable ? 'hover:border-pelip-border-strong cursor-pointer' : ''}`}
                onClick={() => isClickable && navigate(`/?job=${best.id}`)}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-600/10 flex items-center justify-center shrink-0">
                      <Clapperboard size={20} className="text-blue-700 dark:text-blue-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-pelip-text truncate">{name}</h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-pelip-text-faint">
                        <span>{formatDate(best.created_at)}</span>
                        {scenesText && <span>· {scenesText}</span>}
                        {(best.version ?? 1) > 1 && <span>· v{best.version}</span>}
                        {versions.length > 1 && (
                          <button
                            className="inline-flex items-center gap-0.5 hover:text-pelip-text underline decoration-dotted"
                            onClick={(e) => { e.stopPropagation(); setExpandedKey(isExpanded ? null : projKey); }}
                          >
                            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            {versions.length} versiones
                          </button>
                        )}
                        {user?.id && best.user_id && best.user_id !== user.id && (
                          <span className="text-pelip-accent">· de {ownerNames?.[best.user_id] || 'un compañero'}</span>
                        )}
                      </div>
                      {bestChip && (
                        <div className="mt-1 text-xs text-pelip-text-muted flex items-center gap-1">
                          <GitBranch size={11} className="text-pelip-accent shrink-0" />
                          <span className="truncate">{bestChip}</span>
                        </div>
                      )}
                    </div>

                    <Badge className={`${status.color} gap-1 shrink-0`}>
                      {status.icon}
                      {status.label}
                    </Badge>

                    {best.status === 'processing' || best.status === 'needs_continue' ? (
                      <div className="w-16 text-right">
                        <span className="text-sm font-mono text-blue-700 dark:text-blue-400">{best.progress}%</span>
                      </div>
                    ) : null}

                    {best.status === 'completed' && !isArchived && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-pelip-text-faint hover:text-pelip-accent shrink-0"
                        title="Subir nueva versión (reutiliza lo que no cambie; no consume plaza)"
                        onClick={(e) => { e.stopPropagation(); navigate(`/subir?parent_job_id=${best.id}`); }}
                      >
                        <GitBranch size={14} />
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-pelip-text-faint hover:text-pelip-text shrink-0"
                      title={isArchived ? 'Reactivar proyecto' : 'Archivar (libera una plaza de proyecto)'}
                      onClick={(e) => { e.stopPropagation(); setArchived(projectJobs.map((j: any) => j.id), !isArchived); }}
                    >
                      {isArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    </Button>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-pelip-text-faint hover:text-red-400 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminar análisis</AlertDialogTitle>
                          <AlertDialogDescription>
                            Se eliminarán todos los datos de "{name}" (escenas, personajes, presupuesto, etc.)
                            {versions.length > 1 ? `, incluidas sus ${versions.length} versiones` : ''}. Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(projectJobs.map((j: any) => j.id))}>
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  {isExpanded && versions.length > 1 && (
                    <div className="mt-4 pt-3 border-t border-pelip-border space-y-1.5">
                      {versions.map((v: any) => {
                        const vStatus = statusConfig[v.status] || statusConfig.pending;
                        const vChip = diffChipText(v);
                        const vClickable = v.status === 'completed';
                        return (
                          <div
                            key={v.id}
                            className={`flex items-center gap-3 text-xs rounded-md px-2 py-1.5 ${vClickable ? 'hover:bg-pelip-bg-elev-2 cursor-pointer' : ''} ${v.id === best.id ? 'bg-pelip-bg-elev-2' : ''}`}
                            onClick={(e) => { e.stopPropagation(); if (vClickable) navigate(`/?job=${v.id}`); }}
                          >
                            <span className="font-mono text-pelip-text-muted w-8 shrink-0">v{v.version ?? 1}</span>
                            <span className="text-pelip-text-faint shrink-0">{formatDate(v.created_at)}</span>
                            <Badge className={`${vStatus.color} gap-1 shrink-0`}>
                              {vStatus.icon}
                              {vStatus.label}
                            </Badge>
                            {v.reanalysis_mode === 'incremental' && vChip && (
                              <span className="text-pelip-text-faint truncate">· {vChip}</span>
                            )}
                            {v.id === best.id && <span className="text-pelip-accent shrink-0 ml-auto">actual</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
